<script setup lang="ts">
    import { computed, watch, onMounted, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import CorporateNavigation from "@common/Components/CorporateNavigation.vue"
    import ScenarioNavigation from '@Quest/CorporateScenarios/Components/ScenarioNavigation.vue'
    import PnlViewer from '@Quest/Pnls/Components/PnlViewer.vue'

    import { CurrencyDenomination } from '@common/types'

    import
        {
            usePnlPageQueryV1,
            useCorporateScenarioPnlQueryV1
        } from '@Quest/CorporateScenarios/queries-vue-composables'

    import type {
        CorporateScenarioPnlReadModel,
        PnlPageReadModel
    } from '@Quest/CorporateScenarios/readmodels'

    import type { PnlTemplateReadModel } from '@Quest/Pnls/readmodels'


    // ======================
    // Route
    // ======================

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))
    const scenarioId = computed(() => String(route.params.scenarioId ?? ''))


    // ======================
    // Queries
    // ======================

    const {
        runPnlPageQueryV1,
        modelPnlPageQueryV1
    } = usePnlPageQueryV1(workspaceId, strategyId, scenarioId)

    const rm = modelPnlPageQueryV1 as Ref<PnlPageReadModel | null>
    const applyBenefitsConfidenceFactor = computed(() => rm.value?.ApplyBenefitsConfidenceFactor)
    const pnlTemplateId = computed(() => rm.value?.PnlTemplateId)
    const denomination = computed(() => rm.value?.Denomination)


    const {
        runCorporateScenarioPnlQueryV1,
        modelCorporateScenarioPnlQueryV1
    } = useCorporateScenarioPnlQueryV1(workspaceId, strategyId, scenarioId, applyBenefitsConfidenceFactor, pnlTemplateId, denomination)


    // ======================
    // Derived state
    // ======================

    const selectedPnl = computed<PnlTemplateReadModel | null>(() =>
    {
        if (!rm.value?.PnlTemplates) return null

        if (rm.value.PnlTemplateId)
        {
            return rm.value.PnlTemplates.find(a => a.Id === rm.value!.PnlTemplateId) ?? null
        }

        return rm.value.PnlTemplates.find(a => a.IsDefault === true) ?? null
    })


    // ======================
    // Behaviour
    // ======================

    function reloadPnl()
    {
        if (rm.value?.PnlTemplateId)
        {
            runCorporateScenarioPnlQueryV1()
            rm.value.Report = null
        }
    }

    function toggleBenefitsConfidenceFactor()
    {
        if (!rm.value) return
        rm.value.ApplyBenefitsConfidenceFactor = !rm.value.ApplyBenefitsConfidenceFactor
        reloadPnl()
    }

    function setDenomination(d: CurrencyDenomination)
    {
        if (!rm.value) return
        rm.value.Denomination = d
        reloadPnl()
    }

    function toggleCagrVisible()
    {
        if (!rm.value) return
        rm.value.CagrVisible = !rm.value.CagrVisible
    }

    function switchPnlTemplate(pnlId: string | null)
    {
        if (!rm.value) return
        rm.value.PnlTemplateId = pnlId
        reloadPnl()
    }

    function onScheduled()
    {
        runPnlPageQueryV1()
    }


    // ======================
    // Replace onSetModel
    // ======================

    watch(modelCorporateScenarioPnlQueryV1, (model) =>
    {
        if (model && rm.value)
        {
            const pnl = model as CorporateScenarioPnlReadModel
            rm.value.Report = pnl.Report
        }
    })


    // ======================
    // Lifecycle
    // ======================

    onMounted(() =>
    {
        document.title = "Enterprise Plan - P&L"
        runPnlPageQueryV1()
    })
</script>

<template>
    <div>
        <CorporateNavigation ActivePage="dashboard"></CorporateNavigation>

        <div v-if="rm" class="container-fluid" style="margin-top: 80px; padding-top: 20px;">
            <div class="row">
                <div class="col-lg-6">
                    <small class="text-muted">Enterprise Plan: {{rm.ScenarioName}}</small>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">P&L</h1>
                </div>
                <div class="col-lg-6 d-flex flex-column justify-content-end">
                    <ScenarioNavigation @scheduled="onScheduled" />
                </div>
            </div>

            <hr />

            <div class="toolbar mt-5">
                <div>
                    <button type="button" class="btn mr-1" :class="rm.ApplyBenefitsConfidenceFactor ? 'btn-warning' : 'btn-outline-secondary'" @click="toggleBenefitsConfidenceFactor()">
                        <i class="fa-regular" :class="rm.ApplyBenefitsConfidenceFactor ? 'fa-square-check' : 'fa-square'"></i>
                        Risk adjusted benefits
                    </button>

                    <button type="button" class="btn mr-1" :class="rm.CagrVisible ? 'btn-info' : 'btn-outline-secondary'" @click="toggleCagrVisible()">
                        <i class="fa-regular" :class="rm.CagrVisible ? 'fa-square-check' : 'fa-square'"></i>
                        CAGR
                    </button>

                    <div class="btn-group mr-1">
                        <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown">
                            {{rm.Denomination}}s
                        </button>
                        <div class="dropdown-menu">
                            <a class="dropdown-item" @click="setDenomination('Million');">Millions</a>
                            <a class="dropdown-item" @click="setDenomination('Thousand');">Thousands</a>
                            <a class="dropdown-item" @click="setDenomination('Unit');">Units</a>
                        </div>
                    </div>

                    <div class="btn-group">
                        <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">P&L: {{selectedPnl?.Name}}</button>
                        <div class="dropdown-menu dropdown-menu-right">
                            <a class="dropdown-item" v-for="t in rm.PnlTemplates" :key="t.Id" @click="switchPnlTemplate(t.Id)">{{t.Name}}</a>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="rm.Report">
                <PnlViewer :readModel="rm.Report" :cagrVisible="rm.CagrVisible" class="mt-3"></PnlViewer>
            </div>

        </div>
    </div>
</template>

<style scoped>
    .toolbar {
        background-color: #f8f8f8; /* Light gray background for the toolbar */
        padding: 10px 15px; /* Padding around the toolbar */
        border: 1px solid #e4e4e4; /* A light border to give separation */
        display: flex; /* Use flex to give layout flexibility */
        align-items: center; /* Center-align items vertically */
        justify-content: space-between; /* Separate items */
    }
</style>
